import { type ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  /** Custom footer (typically save/cancel buttons). */
  footer?: ReactNode;
  /** Modal width. Default 'md'. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const sizeMap = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

/**
 * Accessible modal — closes on Escape, click outside, or close button.
 * Renders in a portal to escape parent overflow/transform contexts.
 */
export function Modal({ open, onClose, title, description, children, footer, size = 'md' }: ModalProps) {
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Auto-focus the close button so screen-readers announce the modal.
    closeBtnRef.current?.focus();
    // Lock body scroll.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        // Backdrop click closes — but only if mousedown started on the backdrop
        // (prevents accidental close when dragging out of an input).
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={cn(
          'w-full overflow-hidden rounded-lg bg-white shadow-card-hover',
          sizeMap[size],
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-brand-gray-light px-6 py-4">
          <div>
            <h2 id="modal-title" className="text-base font-semibold text-brand-charcoal">
              {title}
            </h2>
            {description && (
              <p className="mt-0.5 text-sm text-brand-gray-mid">{description}</p>
            )}
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-1 rounded-md p-2 text-brand-gray-mid hover:bg-brand-off-white hover:text-brand-charcoal"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </header>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>

        {footer && (
          <footer className="flex justify-end gap-2 border-t border-brand-gray-light bg-brand-off-white px-6 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
